import { Button, Divider, Icon, List, Modal, Spinner } from '@ui-kitten/components';
import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { ListRenderItemInfo, Text, TouchableOpacity, StyleSheet, View } from 'react-native';
import ExtensionBadge from '../ExtensionBadge';
import { ProgressBar, Popover, DNAText } from '@alucio/lux-ui';
import { bytesToSize } from 'src/utils/documentHelpers';

// AMPLIFY
import API, { graphqlOperation } from '@aws-amplify/api';
import Storage from '@aws-amplify/storage'
import {
  CreateDocumentFromS3UploadInput,
  CreateDocumentFromS3UploadMutation,
  CreateUserDocumentFromS3UploadMutation,
} from '@alucio/aws-beacon-amplify/src/API';
import {
  createDocumentFromS3Upload,
  createUserDocumentFromS3Upload,
} from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { GraphQLResult } from '@aws-amplify/api-graphql';

const styles = StyleSheet.create({
  actionButton: {
    marginRight: 5,
    maxHeight: 37,
    maxWidth: 80,
  },
  actionButtonBackground: {
    backgroundColor: '#0095FF',
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  badge: {
    zIndex: 1,
  },
  defaultIconColor: {
    color: 'gray',
  },
  errorIconColor: {
    color: '#CB3831',
  },
  flexEnd: {
    justifyContent: 'flex-end',
  },
  itemField: {
    flexDirection: 'row',
    paddingLeft: 5,
    paddingRight: 5,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  grayColor: {
    color: 'gray',
  },
  largeFlex: {
    flex: 8,
  },
  listItemWrapper: {
    backgroundColor: 'white',
    height: 45,
    justifyContent: 'space-between',
    paddingLeft: 15,
    paddingRight: 15,
  },
  marginLeft: {
    marginLeft: 30,
  },
  mediumFlex: {
    flex: 6,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 5,
    maxHeight: 365,
    minWidth: 650,
  },
  modalHeader: {
    justifyContent: 'space-between',
    padding: 15,
  },
  smallFlex: {
    flex: 2,
  },
  statusTextWrapper: {
    flexDirection: 'column',
    marginLeft: 15,
  },
  statusIcon: {
    height: 22,
    width: 22,
  },
  statusText: {
    fontWeight: 'bold',
    fontSize: 17,
  },
  successIconColor: {
    color: '#35C895',
  },
  warningIconColor: {
    color: '#FFAA00',
  },
  xSmallFlex: {
    flex: 1,
  },
});

const supportedExtensions = ['PPTX', 'PDF'];
const MAX_USER_FILE_SIZE = 10485760; // 10 MB

interface Props {
  files: File[];
  closeModal: () => void;
  isUserDocument?: boolean;
}

interface FilesDetails {
  index: number;
  filename: string;
  extension: string;
  formattedSize: string;
  size: number;
  uploadingPercentage: number;
  isValidExtension: boolean;
  status: 'CANCELED' | 'PENDING' | 'UPLOADING' | 'ERROR' | 'UPLOADED';
}

function UploadDocumentsModal(props: Props) {
  const { closeModal, files, isUserDocument } = props;
  const [isUploading, setIsUploading] = useState<boolean>(true);
  const [filesDetails, setFilesDetails] = useState<FilesDetails[]>(formatFiles(files));
  const validExtensionFilesLength = filesDetails.filter(({ isValidExtension }) => isValidExtension).length;
  const isCanceled = useRef(false);
  const uploadedSize = useRef<number>(0);
  const uploadedFiles = useRef<number>(0);
  const lastProgress = useRef<number>(0);
  const uploadErrors = useRef<any>([]);
  const uploadStatusText =
    getUploadStatusText(uploadedFiles.current, uploadErrors.current.length, validExtensionFilesLength, isUploading);
  const totalSize = filesDetails.reduce((acc, { isValidExtension, size, status }) => {
    return status === 'CANCELED' || !isValidExtension ? acc : acc + size;
  }, 0);

  useEffect(() => {
    uploadFiles();
  }, []);

  async function uploadFile(file: File, index: number) {
    try {
      if (filesDetails[index].status === 'CANCELED' || isCanceled.current || !filesDetails[index].isValidExtension) {
        return;
      }

      // Update the status of the uploading file
      setFilesDetails(getUpdatedFileDetails(filesDetails, index, 'UPLOADING'));

      const { size } = file;
      const fileExtension = file.name.split('.').pop();
      const key = `publish_tmp/${Date.now()}.${fileExtension}`;

      // Check if user file exceeds the limit
      if (size > MAX_USER_FILE_SIZE && isUserDocument) {
        uploadErrors.current[index] = 'This file is too large for upload. The maximum supported file size is 10MB.';
        setFilesDetails(getUpdatedFileDetails(filesDetails, index, 'ERROR'));
        return;
      }

      // Store the file on S3
      lastProgress.current = 0;
      await Storage.put(key, file, {
        contentType: file!.type,
        contentDisposition: `attachment; filename="${file.name}"`,
        level: 'private',
        progressCallback(progress) {
          if (isCanceled.current) {
            return;
          }
          // The idea of calculating on 95% instead of 100% is because after the file is updloaded, the api needs to be called.
          // Therefore, the user won't see the bar completed since there's still an operation being performed.
          const percentageUploaded = (progress.loaded * 95) / size;
          const rounded = percentageUploaded.toFixed(2);

          // Update the uploaded percentage of the uploading file
          setFilesDetails(filesDetails.map((file, idx) => {
            if (idx === index) {
              file.uploadingPercentage = Number.parseFloat(rounded);
            }
            return file
          }));

          // Update the total content uploaded
          uploadedSize.current = (uploadedSize.current - lastProgress.current) + progress.loaded;
          lastProgress.current = progress.loaded;
        },
      });

      if (isCanceled.current) {
        // Since it was cancelled but can't stop the put, proceed to delete the item
        Storage.remove(key);
        return;
      }
      const apiIinputObj: CreateDocumentFromS3UploadInput = {
        srcFilename: file.name,
        fileS3Key: `${key}`,
      };

      if (isUserDocument) {
        const result = await (API.graphql(
          graphqlOperation(createUserDocumentFromS3Upload, {
            inputDoc: apiIinputObj,
          }),
        )) as GraphQLResult<CreateUserDocumentFromS3UploadMutation>;
        analytics?.track('VIEWER_DOCUMENT_CREATE', {
          action: 'CREATE',
          category: 'VIEWER_DOCUMENT',
          documentId: result.data?.createUserDocumentFromS3Upload?.documentId,
          documentVersionId: result.data?.createUserDocumentFromS3Upload?.id,
        });
      }
      else {
        const result = await (API.graphql(
          graphqlOperation(createDocumentFromS3Upload, {
            inputDoc: apiIinputObj,
          }),
        )) as GraphQLResult<CreateDocumentFromS3UploadMutation>;
        analytics?.track('DOCUMENT_CREATE', {
          action: 'CREATE',
          category: 'DOCUMENT',
          documentId: result.data?.createDocumentFromS3Upload?.documentId,
          documentVersionId: result.data?.createDocumentFromS3Upload?.id,
        });
      }

      // Change the file's status to uploaded
      setFilesDetails(filesDetails.map((file, idx) => {
        if (idx === index) {
          file.uploadingPercentage = 100;
          file.status = 'UPLOADED';
        }
        return file
      }));

      // Update the number of already uploaded files
      uploadedFiles.current += 1;
    } catch (e) {
      console.error(e);
      // If the error is not due a Network Error, the user should see a Generic message
      if (e.message && e.message.includes('Network Error')) {
        uploadErrors.current[index] = e.message;
      } else {
        uploadErrors.current[index] = 'An unexpected error occurred';
      }

      // Update the status of the file to Error
      setFilesDetails(getUpdatedFileDetails(filesDetails, index, 'ERROR'));
    }
  }

  async function uploadFiles(): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      await uploadFile(files[i], i)
    }

    if (!isCanceled.current)
    { setIsUploading(false); }
  }

  function cancelUpload(): void {
    isCanceled.current = true;
    closeModal();
  }

  // Update the status of a file to Canceled
  function cancelFile(index: number): void {
    setFilesDetails(getUpdatedFileDetails(filesDetails, index, 'CANCELED'));
  }

  function renderListItem({ item: file }: ListRenderItemInfo<FilesDetails>): ReactElement<View> {
    const showProgressBar = ['PENDING', 'UPLOADING'].includes(file.status) && file.isValidExtension;
    const fileNameSpace = showProgressBar ? styles.mediumFlex : styles.largeFlex;
    return (
      <View style={[styles.listItemWrapper, styles.itemRow]}>
        <View style={[styles.itemField, styles.xSmallFlex, styles.badge]}>
          <ExtensionBadge extension={file.extension} />
        </View>
        <View style={[styles.itemField, fileNameSpace]}>
          <Text numberOfLines={1}>{file.filename}</Text>
        </View>
        {showProgressBar && (
          <View style={styles.smallFlex}>
            <ProgressBar finalValue={100} progress={file.uploadingPercentage} />
          </View>
        )}
        <View style={[styles.itemField, styles.smallFlex, styles.flexEnd]}>
          <Text>{file.formattedSize}</Text>
        </View>
        {getStatusAction(file, () => cancelFile(file.index), uploadErrors)}
      </View>
    );
  }

  function renderModalHeader(): ReactElement<View> {
    const uploadedPercentage = ((uploadedSize.current * 100) / totalSize).toFixed(2);
    return (
      <View style={[styles.itemRow, styles.modalHeader]}>
        <View style={styles.itemRow}>
          {isUploading && <Spinner status="success" />}
          <View style={styles.statusTextWrapper}>
            <Text style={styles.statusText}>{uploadStatusText}</Text>
            {isUploading && <Text style={styles.grayColor}>Progress: {uploadedPercentage}%</Text>}
          </View>
        </View>
        <View style={[styles.itemRow, styles.marginLeft]}>
          {
            isUploading && <Button
              // @ts-ignore
              style={[styles.actionButton, styles.actionButtonBackground]}
              onPress={cancelUpload}
            >Cancel
            </Button>
          }
          <Button
            // @ts-ignore
            style={[styles.actionButton, !isUploading && styles.actionButtonBackground]}
            disabled={isUploading}
            onPress={closeModal}
          >
            Close
          </Button>
        </View>
      </View>
    );
  }

  return (
    <Modal visible={true} backdropStyle={styles.backdrop}>
      <View style={styles.modal}>
        {renderModalHeader()}
        <ProgressBar finalValue={totalSize} progress={uploadedSize.current} />
        <Divider />
        <List
          style={{ overflow: 'visible' }}
          data={filesDetails}
          ItemSeparatorComponent={Divider}
          renderItem={renderListItem}
        />
      </View>
    </Modal>
  );
}

// Returns a list of files formatted to be rendered
function formatFiles(files: File[]): FilesDetails[] {
  return files.map((file: File, idx) => {
    const extension = (file.name.split('.').pop() || '').toUpperCase();
    return {
      filename: file.name.substring(0, file.name.lastIndexOf('.')),
      extension,
      formattedSize: bytesToSize(file.size),
      size: file.size,
      uploadingPercentage: 0,
      status: idx === 0 ? 'UPLOADING' : 'PENDING',
      index: idx,
      isValidExtension: supportedExtensions.includes(extension),
    };
  });
}

// According to the file's status will return an icon
function getStatusAction(file: FilesDetails, callback: () => void, uploadErrors): ReactElement<View> {
  if (!file.isValidExtension) {
    return <View style={[styles.itemField, styles.xSmallFlex]} />;
  }

  switch (file.status) {
    case 'CANCELED':
      return (
        <View style={[styles.itemField, styles.xSmallFlex, styles.flexEnd]}>
          <Icon
            style={[styles.statusIcon, styles.warningIconColor]}
            name="alert"
          />
        </View>
      );

    case 'UPLOADING':
      return (
        <View style={[styles.itemField, styles.xSmallFlex, styles.flexEnd]}>
          <Spinner
            // @ts-ignore
            style={[styles.statusIcon]}
            status="success"
          />
        </View>
      );

    case 'UPLOADED':
      return (
        <View style={[styles.itemField, styles.xSmallFlex, styles.flexEnd]}>
          <Icon
            style={[styles.statusIcon, styles.successIconColor]}
            name="check-circle"
          />
        </View>
      );

    case 'ERROR': {
      const networkError = uploadErrors.current[file.index].includes('Network');
      console.error(uploadErrors.current[file.index]);
      return (
        <Popover>
          <Popover.Anchor>
            <Icon
              style={[styles.statusIcon, networkError ? styles.defaultIconColor : styles.errorIconColor]}
              name={networkError ? 'cloud-off-outline' : 'information-outline'}
            />
          </Popover.Anchor>
          <Popover.Content>
            <DNAText status="basic">{uploadErrors.current[file.index]}</DNAText>
          </Popover.Content>
        </Popover>
      );
    }
    default:
      return (
        <View style={[styles.itemField, styles.xSmallFlex, styles.flexEnd]}>
          <TouchableOpacity onPress={() => callback()}>
            <Icon
              style={[styles.statusIcon, styles.defaultIconColor]}
              name="close"
            />
          </TouchableOpacity>
        </View>
      );
  }
}

// Returns the header text according to progress
function getUploadStatusText(uploadedFiles: number, errors: number, totalFiles: number, isUploading: boolean): string {
  let statusText = '';

  if (uploadedFiles === totalFiles || !isUploading) {
    statusText = `Uploaded ${uploadedFiles} item(s)`;
  } else {
    statusText = `Uploading ${uploadedFiles + 1} of ${totalFiles} item(s)`;
  }

  if (errors > 0) {
    statusText += ` - ${errors} error(s)`;
  }

  return statusText;
}

// Returns the file details list with the status updated of a specific file
function getUpdatedFileDetails(files: FilesDetails[], index: number, status: FilesDetails['status']): FilesDetails[] {
  return files.map((file, idx) => {
    if (idx === index) {
      file.status = status;
    }
    return file;
  });
}

export default UploadDocumentsModal;
